import { Button } from "@design";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";

/**
 * The unified save bar for Build entity tabs (overview forms + capabilities).
 *
 * Pinned to the bottom of the scrolling content (`sticky bottom-4` inside the
 * AppLayout scroll container), so the save action is always visible — even at
 * the bottom of a long list — instead of buried after it. Shows an "Unsaved
 * changes" indicator so toggling a capability and not saving is obvious.
 *
 * Every editable tab uses this, so the save action is in the same place with
 * the same look across the app.
 */
export function SaveBar({
  dirty,
  saving,
  onSave,
  onCancel,
  saveLabel,
  requireChanges = true,
}: Readonly<{
  dirty: boolean;
  saving?: boolean;
  onSave: () => void;
  onCancel?: () => void;
  saveLabel?: string;
  /** Disable Save until something changes (editing). Off when creating: a pre-filled create form must be submittable as-is. */
  requireChanges?: boolean;
}>) {
  const { t } = useTranslation("build");
  return (
    <div className="sticky bottom-4 z-10 mt-2">
      <div className="flex items-center justify-end gap-3 rounded-xl border border-border-subtle bg-surface-primary px-4 py-3 shadow-sm">
        {dirty && (
          <Text variant="body-sm" tone="tertiary" className="mr-auto">
            {t("saveBar.unsaved")}
          </Text>
        )}
        {onCancel && (
          <Button variant="text" size="sm" onClick={onCancel}>
            {t("saveBar.cancel")}
          </Button>
        )}
        <Button
          variant="primary"
          size="sm"
          onClick={onSave}
          loading={saving}
          disabled={(requireChanges && !dirty) || saving}
        >
          {saving ? t("saveBar.saving") : (saveLabel ?? t("saveBar.save"))}
        </Button>
      </div>
    </div>
  );
}
